import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { I18nService } from '../core/i18n.service';
import { SessionService } from '../core/session.service';
import { TeamsApiService } from '../core/teams-api.service';
import { TeamPayload } from '../core/teams-api.types';

@Component({
  selector: 'app-player-team-detail-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <section class="page">
      <p class="back-link"><a routerLink="/player/teams">{{ t('player.teamDetail.back') }}</a></p>

      @if (loading()) {
        <p class="state">{{ t('player.teamDetail.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (team(); as teamData) {
        <header class="team-head">
          <div>
            <h1>{{ teamData.name }}</h1>
            <p class="meta">{{ t('player.teamDetail.myRole') }} <strong>{{ myRoleLabel(teamData) }}</strong></p>
            @if (teamData.logo_url) {
              <p class="meta">{{ t('player.teamDetail.logo', { value: teamData.logo_url }) }}</p>
            }
          </div>
          <p class="size-badge">{{ teamData.member_count }} / {{ teamData.max_members }}</p>
        </header>

        @if (canManageRoster(teamData)) {
          <section class="invite-card">
            <h2>{{ t('player.teamDetail.inviteTitle') }}</h2>
            <p class="meta">{{ t('player.teamDetail.inviteSubtitle') }}</p>

            @if (inviteError()) {
              <p class="state state--error">{{ inviteError() }}</p>
            }
            @if (inviteSuccess()) {
              <p class="state state--success">{{ inviteSuccess() }}</p>
            }

            <button type="button" [disabled]="creatingInviteLink()" (click)="generateInvitationLink(teamData.id)">
              {{ creatingInviteLink() ? t('player.teamDetail.generating') : (lastInvitationLink() ? t('player.teamDetail.inviteRegenerate') : t('player.teamDetail.inviteGenerate')) }}
            </button>

            @if (lastInvitationLink()) {
              <p class="invite-url">{{ lastInvitationLink() }}</p>
            }
          </section>
        }

        <section class="roster-section">
          <h2>{{ t('player.teamDetail.rosterTitle') }}</h2>
          @if (rosterActionError()) {
            <p class="state state--error">{{ rosterActionError() }}</p>
          }
          @if (rosterActionSuccess()) {
            <p class="state state--success">{{ rosterActionSuccess() }}</p>
          }
        </section>

        <section class="tournament-hub">
          <h2>{{ t('player.teamDetail.tournamentHubTitle') }}</h2>
          <p class="meta">
            {{ canManageRoster(teamData) ? t('player.teamDetail.tournamentHubManageable') : t('player.teamDetail.tournamentHubNotManageable') }}
          </p>
          <a class="tournament-link" routerLink="/tournaments">{{ t('player.teamDetail.tournamentHubCta') }}</a>
        </section>

        <ul class="roster-grid">
          @for (member of teamData.roster; track member.user_id) {
            <li class="roster-card">
              <div>
                <p class="member-name">{{ member.pseudo }}</p>
                <p class="member-role">{{ roleLabel(member.role) }}</p>
              </div>
              @if (canRemoveMember(teamData, member.user_id, member.role)) {
                <button
                  type="button"
                  class="remove-btn"
                  [disabled]="removingMemberId() === member.user_id"
                  (click)="removeMember(teamData.id, member.user_id)"
                >
                  {{ removingMemberId() === member.user_id ? t('player.teamDetail.removing') : t('player.teamDetail.remove') }}
                </button>
              }
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 900px; margin: 0 auto; }
    .back-link { margin: 0 0 0.6rem; }
    .team-head { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; border: 1px solid var(--line-soft); border-radius: 12px; padding: 0.9rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    h1 { margin: 0 0 0.45rem; font-size: 1.5rem; color: var(--text-strong); }
    h2 { margin: 1rem 0 0.5rem; font-size: 1.1rem; }
    .meta { margin: 0.2rem 0; color: var(--text-muted); }
    .meta strong { color: var(--text-strong); }
    .size-badge { margin: 0; border: 1px solid rgba(138, 184, 255, 0.45); border-radius: 999px; padding: 0.25rem 0.7rem; color: #dce9ff; font-size: 0.85rem; font-weight: 700; white-space: nowrap; }
    .invite-card { margin: 1rem 0; border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.8rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); }
    .invite-card button { margin-top: 0.4rem; border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .invite-url { margin-top: 0.65rem; padding: 0.5rem; border-radius: 8px; background: rgba(10, 18, 31, 0.8); color: var(--text-strong); font-size: 0.92rem; word-break: break-all; }
    .roster-section { display: flex; flex-direction: column; gap: 0.35rem; }
    .tournament-hub { margin: 0.7rem 0 0.9rem; border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(21, 33, 53, 0.85) 0%, rgba(14, 23, 37, 0.82) 100%); }
    .tournament-link { display: inline-flex; margin-top: 0.45rem; border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.45rem 0.7rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; text-decoration: none; }
    .roster-grid { margin: 0; padding: 0; list-style: none; display: grid; gap: 0.65rem; }
    .roster-card { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: rgba(14, 23, 37, 0.8); display: flex; align-items: center; justify-content: space-between; gap: 0.7rem; }
    .member-name { margin: 0; color: var(--text-strong); font-weight: 700; }
    .member-role { margin: 0.2rem 0 0; color: var(--text-muted); font-size: 0.9rem; }
    .remove-btn { border: 1px solid rgba(255, 120, 120, 0.5); border-radius: 8px; background: rgba(172, 45, 45, 0.2); color: #ffdede; padding: 0.42rem 0.68rem; font-weight: 700; }
    .remove-btn:disabled { opacity: 0.65; }
    .state--error { color: var(--danger); }
    .state--success { color: #2b8f4b; }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class PlayerTeamDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly teamsApi = inject(TeamsApiService);
  private readonly session = inject(SessionService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly team = signal<TeamPayload | null>(null);
  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly creatingInviteLink = signal(false);
  protected readonly inviteError = signal<string | null>(null);
  protected readonly inviteSuccess = signal<string | null>(null);
  protected readonly lastInvitationLink = signal<string | null>(null);
  protected readonly removingMemberId = signal<number | null>(null);
  protected readonly rosterActionError = signal<string | null>(null);
  protected readonly rosterActionSuccess = signal<string | null>(null);

  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => this.teamsApi.getTeam(Number(params.get('id')))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ team }) => {
          this.team.set(team);
          this.loading.set(false);
        },
        error: (error) => {
          if (error?.status === 403) {
            this.errorMessage.set(this.t('player.teamDetail.forbidden'));
          } else {
            this.errorMessage.set(this.t('player.teamDetail.loadError'));
          }
          this.loading.set(false);
        }
      });
  }

  protected canManageRoster(teamData: TeamPayload): boolean {
    const currentUserId = this.session.identity()?.id;
    if (!currentUserId) {
      return false;
    }

    const membership = teamData.roster.find((member) => member.user_id === currentUserId);
    return membership?.role === 'captain' || membership?.role === 'manager_equipe';
  }

  protected myRole(teamData: TeamPayload): string | null {
    const currentUserId = this.session.identity()?.id;
    if (!currentUserId) {
      return null;
    }

    const membership = teamData.roster.find((member) => member.user_id === currentUserId);
    return membership?.role ?? null;
  }

  protected myRoleLabel(teamData: TeamPayload): string {
    return this.roleLabel(this.myRole(teamData));
  }

  protected roleLabel(role: string | null | undefined): string {
    switch (role) {
      case 'captain':
        return this.t('player.teamDetail.role.captain');
      case 'manager_equipe':
        return this.t('player.teamDetail.role.manager');
      default:
        return this.t('player.teamDetail.role.player');
    }
  }

  protected generateInvitationLink(teamId: number): void {
    this.creatingInviteLink.set(true);
    this.inviteError.set(null);
    this.inviteSuccess.set(null);

    this.teamsApi
      .createInvitationLink(teamId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ invitation }) => {
          const absoluteLink = `${window.location.origin}${invitation.invite_path}`;
          this.lastInvitationLink.set(absoluteLink);
          this.inviteSuccess.set(this.t('player.teamDetail.inviteReady'));
          this.creatingInviteLink.set(false);
        },
        error: (error) => {
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.inviteError.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0
                ? apiErrors.join(' | ')
                : this.t('player.teamDetail.inviteError'))
          );
          this.creatingInviteLink.set(false);
        }
      });
  }

  protected canRemoveMember(teamData: TeamPayload, userId: number, role: string): boolean {
    if (!this.canManageRoster(teamData)) {
      return false;
    }

    const currentUserId = this.session.identity()?.id;
    if (!currentUserId) {
      return false;
    }

    if (userId === currentUserId) {
      return false;
    }

    if (role === 'captain') {
      return false;
    }

    return true;
  }

  protected removeMember(teamId: number, userId: number): void {
    this.removingMemberId.set(userId);
    this.rosterActionError.set(null);
    this.rosterActionSuccess.set(null);

    this.teamsApi
      .removeTeamMember(teamId, userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ team }) => {
          this.team.set(team);
          this.rosterActionSuccess.set(this.t('player.teamDetail.removed'));
          this.removingMemberId.set(null);
        },
        error: (error) => {
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.rosterActionError.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('player.teamDetail.removeError'))
          );
          this.removingMemberId.set(null);
        }
      });
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
